/**
 * World languages via the *browser's* translator — not Google's in-page widget.
 *
 * ⚠ Embedding translate.google.com would require opening `script-src` /
 * `connect-src` to Google, and would send whatever is on the page (including a
 * filled chart) to Google. This site's CSP and privacy model forbid that.
 *
 * Chrome, Edge and Safari already ship a translator. We keep the page in a
 * curated language as the *source*, mark charts / tables so technical terms are
 * not mangled, and ask the reader to use the browser's Translate. That is how
 * "Google Translate" reaches every language without putting Google inside this app.
 *
 * © Manish Jagdish Thatte
 */

/** Languages offered as "translate via browser" targets. Source page stays English. */
export const BROWSER_TRANSLATE_LANGS = Object.freeze([
  { id: 'es', name: 'Español' },
  { id: 'fr', name: 'Français' },
  { id: 'de', name: 'Deutsch' },
  { id: 'pt', name: 'Português' },
  { id: 'bn', name: 'বাংলা' },
  { id: 'ta', name: 'தமிழ்' },
  { id: 'te', name: 'తెలుగు' },
  { id: 'gu', name: 'ગુજરાતી' },
  { id: 'kn', name: 'ಕನ್ನಡ' },
  { id: 'ml', name: 'മലയാളം' },
  { id: 'pa', name: 'ਪੰਜਾਬੀ' },
  { id: 'or', name: 'ଓଡ଼ିଆ' },
  { id: 'ar', name: 'العربية', dir: 'rtl' },
  { id: 'ur', name: 'اردو', dir: 'rtl' },
  { id: 'zh-CN', name: '中文（简体）' },
  { id: 'ja', name: '日本語' },
  { id: 'ko', name: '한국어' },
  { id: 'ru', name: 'Русский' },
  { id: 'id', name: 'Bahasa Indonesia' },
  { id: 'tr', name: 'Türkçe' },
  { id: 'vi', name: 'Tiếng Việt' },
  { id: 'th', name: 'ไทย' },
  { id: 'it', name: 'Italiano' },
  { id: 'nl', name: 'Nederlands' },
  { id: 'pl', name: 'Polski' }
]);

const ALLOWED = new Set(BROWSER_TRANSLATE_LANGS.map((l) => l.id));

export function isBrowserTranslateLang(id) {
  return ALLOWED.has(id);
}

export function browserTranslateName(id) {
  return BROWSER_TRANSLATE_LANGS.find((l) => l.id === id)?.name ?? id;
}

/**
 * Mark chart geometry and tables so browser / Google page-translate skips them.
 * Call after each route render — pages replace `#main` contents.
 */
export function protectTechnicalMarkup(root) {
  if (typeof document === 'undefined') return;
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const sel = [
    'chart-north', 'chart-south', 'chart-east', 'rashi-wheel',
    'planet-table', 'ashtakavarga-grid', 'dasha-tree', 'dasha-timeline',
    'varga-grid', 'guna-table', 'palm-canvas',
    '.planet-table', '.data-table', '.matrix', '.chart',
    '.site-brand__deva', '[data-notranslate]'
  ].join(',');
  for (const el of scope.querySelectorAll(sel)) {
    el.setAttribute('translate', 'no');
    el.classList.add('notranslate');
  }
}

/**
 * Short instruction for the banner. Kept in English on purpose — it is the
 * source language the browser will translate from.
 */
export function browserTranslateHint(targetId) {
  const name = browserTranslateName(targetId);
  return (
    `Page copy is in English so your browser can translate it to ${name}. ` +
    `In Chrome or Edge: right-click the page → “Translate to ${name}” (or use the translate icon in the address bar). ` +
    `In Safari: View → Translation. Charts and śāstra terms stay untranslated on purpose.`
  );
}
